import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { CardFooterComponent } from '../../../shared/card-footer/card-footer.component';
import { HeaderComponent } from '../../../shared/header/header.component';
import { PasswordInputComponent } from '../../../shared/password-input/password-input.component';
import { ApiResponse } from '../../../shared/interfaces/api-response.interface';
import { EmployeeAuthService, LoginResponse, OTPResponse } from '../../../shared/services/employee-auth.service';
import { ErrorMessageService } from '../../../shared/services/error-message.service';
import { RedirectHelperService } from '../../../customidp/shared/services/redirect-helper.service';
import { ActiveSessionService } from '../../../shared/services/active-session.service';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    HeaderComponent,
    CardFooterComponent,
    PasswordInputComponent,
    TranslateModule,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent implements OnInit, OnDestroy {
  email = '';
  password = '';
  emailErrors: string[] = [];
  emailTouched = false;
  passwordErrors: string[] = [];
  passwordTouched = false;
  loginError = '';
  isLoading = false;
  remainingAttempts: number | undefined;

  constructor(
    private titleService: Title,
    private translate: TranslateService,
    private employeeAuthService: EmployeeAuthService,
    private router: Router,
    private errorMessageService: ErrorMessageService,
    private redirectHelper: RedirectHelperService,
    private activeSession: ActiveSessionService
  ) { }

  ngOnInit(): void {
    // PLAI-8323: skip form if another tab already signed in.
    // PLAI-11883: send them to the portal that owns the session, not always
    // the employer portal.
    if (this.activeSession.hasValidToken() && this.activeSession.redirectToActiveSessionHome('N_ME')) {
      return;
    }

    this.titleService.setTitle('Employee | Login');
    this.translate.use(this.translate.currentLang || 'en');

    // Initialize validation states
    this.emailTouched = false;
    this.passwordTouched = false;
    this.emailErrors = [];
    this.passwordErrors = [];
  }

  ngOnDestroy(): void {
  }

  validateEmail() {
    this.emailErrors = [];
    this.emailTouched = true;

    if (!this.email.trim()) {
      this.emailErrors.push('EMPLOYEE.LOGIN.EMAIL_REQUIRED');
    }
  }

  validatePassword() {
    this.passwordErrors = [];
    this.passwordTouched = true;

    if (!this.password) {
      this.passwordErrors.push('EMPLOYEE.LOGIN.PASSWORD_REQUIRED');
    }
  }

  isFormValid(): boolean {
    // Only check if fields are not empty - let API handle format validation
    const isEmailValid = this.email.trim().length > 0;
    const isPasswordValid = this.password.length > 0;

    return isEmailValid && isPasswordValid;
  }

  login() {
    this.loginError = '';

    // Mark fields as touched to show validation errors
    this.emailTouched = true;
    this.passwordTouched = true;

    // Run validations to show required field errors
    this.validateEmail();
    this.validatePassword();

    if (!this.isFormValid()) {
      return;
    }

    this.isLoading = true;

    this.employeeAuthService.validateCredentials(this.email, this.password).subscribe({
      next: (response: ApiResponse<LoginResponse | OTPResponse>) => {
        this.isLoading = false;

        if (this.errorMessageService.isSuccessResponse(response)) {
          if (response.data && 'authCode' in response.data) {
            this.handleLoginSuccess(response.data as OTPResponse, 'employee');
            return;
          }
          if (response.data) {
            const sessionData = {
              sessionToken: (response.data as LoginResponse).sessionId,
              trxId: (response.data as LoginResponse).trxId,
              email: this.email,
            };
            sessionStorage.setItem('employeeSessionData', JSON.stringify(sessionData));
            this.router.navigate(['/employee/account/verify'], { queryParams: { type: 'employee-login' } });
          }
        } else {
          this.handleLoginError(response);
          this.isLoading = false;
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        this.handleLoginError(error);
      }
    });
  }

  private handleLoginSuccess(data: OTPResponse, userType: 'employer' | 'employee'): void {
    const defaultRole = 'N_ME';
    const selectedRole = data?.selectedRole || defaultRole;
    const authCode = data?.authCode;
    const defaultDashboard = '/';

    if (environment.ENV_ACTIVE_PROFILE === 'customIDP' && authCode) {
      this.redirectHelper.redirectByRole(authCode, selectedRole);
    } else {
      this.router.navigate([defaultDashboard]);
    }
  }

  handleLoginError(error: any) {
    // Check for lockout and attempts info
    if (
      (error.errorCode === '1021' || error.errorCode === 'accountTemporarilyDisabled') &&
      error.data && error.data.remainingLockTimeSeconds > 0
    ) {
      const remaining = error.data.remainingLockTimeSeconds;
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      this.translate.get('error.accountTemporarilyDisabled', { minutes, seconds }).subscribe(message => {
        this.loginError = message;
      });
    } else if (error.data && error.data.remainingAttempts !== undefined && error.data.remainingAttempts !== null) {
      this.remainingAttempts = error.data.remainingAttempts;
      this.errorMessageService.getErrorMessage(error.errorCode).subscribe(message => {
        const plural = this.remainingAttempts === 1 ? '' : 's';
        this.translate.get('error.remainingAttempts', { count: this.remainingAttempts, plural }).subscribe(attemptMsg => {
          this.loginError = `${message} ${attemptMsg}`;
        });
      });
    } else if (error.errorCode) {
      this.errorMessageService.getErrorMessage(error.errorCode).subscribe(message => {
        this.loginError = message;
      });
    } else {
      this.translate.get('error.internalServerError').subscribe(message => {
        this.loginError = message;
      });
    }
  }
}
